import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {View, StyleSheet, ViewStyle, TextStyle} from 'react-native';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {ActionButton, Text} from 'ui-kits';
import {StoryDetailsForm, userStoryFormValuesType} from 'components';
import {useSelector, useDispatch} from 'react-redux';
import React, {useCallback, useEffect, useMemo} from 'react';
import {
  selectUserStoryID,
  userStoryFailure,
  userStorySuccess,
  selectUserStoryData,
} from 'redux/selectors/userStorySelector';
import {
  getUserStoryDetailsRequest,
  resetMessage,
  updateUserStoryRequest,
  validateUserStoryRequest,
} from 'redux/slices/userStorySlice';
import {useAuthAccess, useConfimationModal, useToast} from 'hooks';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {selectedProjectID} from 'redux/selectors/projectSelector';
import {getFormattedDate} from 'utils/getFormattedDate';

export function ViewUserStory({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const userStoryID = useSelector(selectUserStoryID);
  const projectID = useSelector(selectedProjectID);
  const getUserStoryDetailsSuccess = useSelector(userStorySuccess);
  const getUserStoryDetailsError = useSelector(userStoryFailure);
  const userStoryData = useSelector(selectUserStoryData);
  const {showToasts} = useToast();
  const {showModal} = useConfimationModal();
  const {hasWriteAccess} = useAuthAccess();

  const initialValues = useMemo(() => {
    return {
      ...userStoryData,
      due_date: userStoryData?.due_date
        ? new Date(userStoryData.due_date)
        : null,
      status: userStoryData?.status?.id || 1,
    };
  }, [userStoryData]);

  const toggleValidation = () => {
    const payload = {
      id: userStoryData?.id!,
      is_validated: !userStoryData?.is_validated,
    };
    dispatch(validateUserStoryRequest(payload));
  };

  function openModal() {
    showModal({
      header: userStoryData?.is_validated
        ? 'Revoke User Story Validation'
        : 'Validate User Story',
      message: userStoryData?.is_validated
        ? 'Are you sure you want to revoke validation for this story?'
        : 'Are you sure you want to validate this story?',
      onConfirmHandler: toggleValidation,
    });
  }

  const handleCancel = useCallback(() => {
    navigation.replace(Routes.PROJECT_BACKLOGS, {
      id: projectID?.toString(),
    } as any);
  }, [navigation, projectID]);

  const handleSave = ({
    created_at,
    updated_at,
    us_id,
    project,
    tags,
    ...values
  }: userStoryFormValuesType) => {
    const payload = {
      ...values,
      due_date:
        values.due_date ??
        getFormattedDate(values.due_date as any, 'yyyy-mm-dd', '-'),
      followers: values.followers.map(item => item?.emp_id),
      responsible: values.responsible?.emp_id,
      id: values.id,
      proj_id: project?.id,
    };
    dispatch(updateUserStoryRequest(payload as any));
  };

  useEffect(() => {
    if (getUserStoryDetailsSuccess) {
      if (getUserStoryDetailsSuccess !== 'S-10021') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(getUserStoryDetailsSuccess as SuccessCodes),
          'success',
        );
      }
      if (getUserStoryDetailsSuccess === 'S-10022') {
        handleCancel();
      }

      if (getUserStoryDetailsSuccess === 'S-10077') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(getUserStoryDetailsSuccess as SuccessCodes),
          'success',
        );
      }

      if (getUserStoryDetailsSuccess === 'S-10078') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(getUserStoryDetailsSuccess as SuccessCodes),
          'success',
        );
      }

      dispatch(resetMessage());
    }

    if (getUserStoryDetailsError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(getUserStoryDetailsError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [
    getUserStoryDetailsError,
    getUserStoryDetailsSuccess,
    dispatch,
    showToasts,
    handleCancel,
    userStoryData,
  ]);

  useEffect(() => {
    if (userStoryID) {
      dispatch(getUserStoryDetailsRequest({id: userStoryID}));
    }
  }, [userStoryID, dispatch]);

  return (
    <View style={styles.container}>
      <View style={styles.headerSection}>
        <Text style={styles.header}>User Story</Text>
        {hasWriteAccess && (
          <ActionButton
            title={
              userStoryData?.is_validated ? 'Revoke Validation' : 'Validate'
            }
            variant="secondary"
            rightIcon={
              userStoryData?.is_validated ? 'revoke_cross' : 'validate_tick'
            }
            onPress={openModal}
            style={
              userStoryData?.is_validated ? styles.revokeButton : undefined
            }
            textStyle={
              userStoryData?.is_validated ? styles.revokeText : undefined
            }
          />
        )}
      </View>
      <StoryDetailsForm
        initialValues={initialValues as any}
        mode="view"
        onCancel={handleCancel}
        onSubmit={handleSave}
      />
    </View>
  );
}
type styleType = {
  container: ViewStyle;
  header: TextStyle;
  revokeText: TextStyle;
  headerSection: ViewStyle;
  revokeButton: ViewStyle;
};
const styles: styleType = StyleSheet.create({
  container: {
    backgroundColor: colors.palette.white,
    flex: 1,
    padding: spacing['4'],
    gap: spacing['4'],
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  header: {
    fontSize: 36,
    color: colors.palette.brandBlue,
    fontWeight: '600',
  },
  revokeButton: {
    borderWidth: 1.5,
    borderColor: colors.palette.revoke,
  },
  revokeText: {
    color: colors.palette.revoke,
  },
});
